import { useEffect } from "react";
import {
  DEFAULT_TWITTER_CARD,
  SITE_NAME,
} from "../utils/seo";

const JSON_LD_SELECTOR = 'script[data-seo-json-ld="true"]';

const upsertMetaTag = ({ name, property }, content) => {
  if (!content) return;

  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    if (name) {
      element.setAttribute("name", name);
    }
    if (property) {
      element.setAttribute("property", property);
    }
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const upsertCanonicalLink = (href) => {
  if (!href) return;

  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const replaceStructuredData = (structuredData) => {
  document.head.querySelectorAll(JSON_LD_SELECTOR).forEach((element) => {
    element.remove();
  });

  const entries = Array.isArray(structuredData)
    ? structuredData.filter(Boolean)
    : structuredData
      ? [structuredData]
      : [];

  entries.forEach((entry) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-json-ld", "true");
    script.textContent = JSON.stringify(entry);
    document.head.appendChild(script);
  });
};

const SeoMetadata = ({
  title,
  description,
  canonicalUrl,
  robots = "index, follow",
  openGraph = {},
  twitter = {},
  structuredData = null,
}) => {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    if (description) {
      upsertMetaTag({ name: "description" }, description);
    }

    upsertMetaTag({ name: "robots" }, robots);
    upsertCanonicalLink(canonicalUrl);

    const resolvedOg = {
      type: "website",
      siteName: SITE_NAME,
      ...openGraph,
    };

    upsertMetaTag({ property: "og:type" }, resolvedOg.type);
    upsertMetaTag({ property: "og:site_name" }, resolvedOg.siteName);
    upsertMetaTag({ property: "og:title" }, resolvedOg.title || title);
    upsertMetaTag(
      { property: "og:description" },
      resolvedOg.description || description
    );
    upsertMetaTag({ property: "og:url" }, resolvedOg.url || canonicalUrl);
    upsertMetaTag({ property: "og:image" }, resolvedOg.image);
    upsertMetaTag({ property: "og:image:alt" }, resolvedOg.imageAlt);

    const resolvedTwitter = {
      card: DEFAULT_TWITTER_CARD,
      ...twitter,
    };

    upsertMetaTag({ name: "twitter:card" }, resolvedTwitter.card);
    upsertMetaTag({ name: "twitter:title" }, resolvedTwitter.title || title);
    upsertMetaTag(
      { name: "twitter:description" },
      resolvedTwitter.description || description
    );
    upsertMetaTag(
      { name: "twitter:image" },
      resolvedTwitter.image || resolvedOg.image
    );
    upsertMetaTag(
      { name: "twitter:image:alt" },
      resolvedTwitter.imageAlt || resolvedOg.imageAlt
    );

    replaceStructuredData(structuredData);
  }, [
    canonicalUrl,
    description,
    openGraph,
    robots,
    structuredData,
    title,
    twitter,
  ]);

  return null;
};

export default SeoMetadata;
